import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Clock, 
  Users, 
  ChevronLeft,
  ChevronRight,
  ShieldCheck,
  AlertCircle,
  Check,
  Sparkles,
  Loader2,
  MapPin
} from 'lucide-react';
import { api } from '../lib/api';
import { useLanguage } from '../i18n/LanguageContext';
import { localizeTour } from '../lib/tourI18n';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const TourDetailPage = () => {
  const { tourId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTour = async () => {
      try {
        const data = await api.getTour(tourId);
        setTour(data);
      } catch (error) {
        console.error('Error loading tour:', error);
        toast.error(t('booking.tourNotFound') || 'Tour not found');
        navigate('/tours');
      } finally {
        setLoading(false);
      }
    };
    fetchTour();
    // Scroll to top when navigating to a tour
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [tourId, navigate, t]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#1D4E41] animate-spin" />
      </div>
    );
  }

  if (!tour) return null;

  const tl = localizeTour(tour, language);
  const hasAddOns = Array.isArray(tour.add_ons) && tour.add_ons.length > 0;
  // Bijagual is priced with a hidden $160 transport base; the tour card should display $20/person only.
  const isBijagual = tour.id === 'tour-bijagual-waterfall';
  const displayPrice = isBijagual ? 20 : tour.price;
  const displayPriceSuffix = isBijagual ? (language === 'es' ? ' / persona' : ' / person') : '';
  const discountedPrice = tour.discount_percent
    ? Math.round(displayPrice * (1 - tour.discount_percent / 100) * 100) / 100
    : null;

  return (
    <div className="min-h-screen bg-[#FAF8F5] flex flex-col">
      <Navbar />

      {/* Hero with image */}
      <section className="relative h-[40vh] md:h-[55vh] w-full overflow-hidden bg-[#1D4E41]">
        <img
          src={tour.image_url}
          alt={tl.title}
          className="w-full h-full object-cover opacity-90"
          data-testid="tour-detail-hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

        {/* Back link */}
        <div className="absolute top-6 left-4 md:left-8 z-10">
          <Link
            to="/tours"
            data-testid="tour-detail-back-link"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-white/95 hover:bg-white text-[#1D4E41] rounded-full text-sm font-medium shadow-md backdrop-blur-sm transition-all"
          >
            <ChevronLeft className="w-4 h-4" />
            {t('booking.backToTours') || (language === 'es' ? 'Tours' : 'Tours')}
          </Link>
        </div>

        {/* Discount badge */}
        {tour.discount_percent ? (
          <div className="absolute top-6 right-4 md:right-8 z-10 px-4 py-2 bg-emerald-600 text-white rounded-full text-sm font-bold shadow-md" data-testid="tour-detail-discount-badge">
            🌿 {language === 'es' ? (tour.discount_label_es || 'Temporada Verde') : (tour.discount_label || 'Green Season')} -{tour.discount_percent}%
          </div>
        ) : null}

        {/* Title overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
          <div className="max-w-5xl mx-auto">
            <span className="inline-block px-3 py-1 bg-[#1D4E41]/90 text-white text-xs uppercase tracking-wider rounded-full mb-3 backdrop-blur-sm" data-testid="tour-detail-category">
              {tour.category}
            </span>
            <h1 className="text-3xl md:text-5xl font-bold text-white drop-shadow-lg" data-testid="tour-detail-title">
              {tl.title}
            </h1>
            <div className="flex items-center gap-4 mt-3 text-white/95 text-sm">
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                {tl.duration}
              </span>
              <span className="flex items-center gap-1.5">
                <Users className="w-4 h-4" />
                {tour.max_passengers === 1 ? t('tours.onePerson') : 
                 tour.max_passengers === 2 ? t('tours.twoPeople') : 
                 `${t('tours.upToLabel') || (language === 'es' ? 'Hasta' : 'Up to')} ${tour.max_passengers}`}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Main content */}
      <main className="flex-1 py-12 px-4 md:px-8">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8">
          {/* Left: full description and details */}
          <div className="space-y-8">
            {/* Description */}
            <section className="bg-white rounded-2xl p-6 md:p-8 shadow-[0_4px_20px_rgb(0,0,0,0.04)] border border-gray-100">
              <h2 className="text-2xl font-bold text-[#1A1A1A] mb-4" data-testid="tour-detail-about-heading">
                {language === 'es' ? 'Sobre este tour' : 'About this tour'}
              </h2>
              <p className="text-gray-700 leading-relaxed whitespace-pre-line" data-testid="tour-detail-description">
                {tl.description}
              </p>
            </section>

            {/* Gallery strip: extra photos of the tour (e.g. crocodiles at the Tárcoles bridge) */}
            {Array.isArray(tour.gallery) && tour.gallery.length > 0 && (
              <section className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="tour-detail-gallery">
                {tour.gallery.map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt={`${tl.title} — extra ${i + 1}`}
                    loading="lazy"
                    className="w-full h-64 md:h-72 object-cover rounded-2xl shadow-[0_4px_20px_rgb(0,0,0,0.08)] border border-gray-100"
                    data-testid={`tour-detail-gallery-img-${i}`}
                  />
                ))}
              </section>
            )}

            {/* Highlights */}
            {tl.highlights && tl.highlights.length > 0 && (
              <section className="bg-white rounded-2xl p-6 md:p-8 shadow-[0_4px_20px_rgb(0,0,0,0.04)] border border-gray-100">
                <h2 className="text-2xl font-bold text-[#1A1A1A] mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#F08A5D]" />
                  {language === 'es' ? 'Lo que incluye' : "What's included"}
                </h2>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="tour-detail-highlights">
                  {tl.highlights.map((highlight, i) => (
                    <li key={i} className="flex items-start gap-2 text-gray-700">
                      <Check className="w-5 h-5 text-[#1D4E41] mt-0.5 shrink-0" />
                      <span>{highlight}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Add-ons */}
            {hasAddOns && (
              <section className="bg-orange-50 border-2 border-[#F08A5D]/30 rounded-2xl p-6 md:p-8" data-testid="tour-detail-addons">
                <h2 className="text-2xl font-bold text-[#1A1A1A] mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#F08A5D]" />
                  {language === 'es' ? 'Extras opcionales' : 'Optional extras'}
                </h2>
                <div className="space-y-3">
                  {tour.add_ons.map((addon) => (
                    <div key={addon.id} className="flex items-start justify-between gap-4 bg-white rounded-xl p-4">
                      <div>
                        <p className="font-semibold text-[#1A1A1A]">
                          {language === 'es' && addon.name_es ? addon.name_es : addon.name}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                          {language === 'es' && addon.description_es ? addon.description_es : addon.description}
                        </p>
                      </div>
                      <span className="shrink-0 px-3 py-1 bg-[#F08A5D] text-white rounded-full font-bold text-sm">
                        +${addon.price_per_person}{addon.vat_applicable ? (language === 'es' ? ' + IVA' : ' + VAT') : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Requirements */}
            {tl.requirements && (
              <section className="bg-white rounded-2xl p-6 md:p-8 shadow-[0_4px_20px_rgb(0,0,0,0.04)] border border-gray-100">
                <h2 className="text-2xl font-bold text-[#1A1A1A] mb-4 flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-[#1D4E41]" />
                  {language === 'es' ? 'Requisitos' : 'Requirements'}
                </h2>
                <p className="text-gray-700 whitespace-pre-line" data-testid="tour-detail-requirements">
                  {tl.requirements}
                </p>
              </section>
            )}

            {/* Cancellation policy */}
            {tl.cancellation_policy && (
              <section className="bg-white rounded-2xl p-6 md:p-8 shadow-[0_4px_20px_rgb(0,0,0,0.04)] border border-gray-100">
                <h2 className="text-2xl font-bold text-[#1A1A1A] mb-4 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                  {language === 'es' ? 'Política de cancelación' : 'Cancellation policy'}
                </h2>
                <p className="text-gray-700 whitespace-pre-line text-sm" data-testid="tour-detail-cancellation">
                  {tl.cancellation_policy}
                </p>
              </section>
            )}
          </div>

          {/* Right: sticky booking card */}
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-gray-100 p-6">
              <div className="mb-4">
                {discountedPrice !== null ? (
                  <>
                    <span className="text-lg text-gray-400 line-through mr-2">${displayPrice}</span>
                    <span className="text-3xl font-bold text-[#1D4E41]" data-testid="tour-detail-price">${discountedPrice}{displayPriceSuffix}</span>
                  </>
                ) : (
                  <span className="text-3xl font-bold text-[#1D4E41]" data-testid="tour-detail-price">${displayPrice}{displayPriceSuffix}</span>
                )}
                <p className="text-sm text-gray-500 mt-1" data-testid="tour-detail-price-note">
                  {language === 'es' ? 'IVA según aplique al checkout' : 'VAT applies at checkout where applicable'}
                </p>
              </div>

              <div className="space-y-2 mb-5 text-sm border-y border-gray-100 py-4">
                <div className="flex items-center justify-between text-gray-700">
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-gray-400" />
                    {language === 'es' ? 'Duración' : 'Duration'}
                  </span>
                  <span className="font-medium">{tl.duration}</span>
                </div>
                <div className="flex items-center justify-between text-gray-700">
                  <span className="flex items-center gap-1.5">
                    <Users className="w-4 h-4 text-gray-400" />
                    {language === 'es' ? 'Capacidad' : 'Capacity'}
                  </span>
                  <span className="font-medium">
                    {tour.max_passengers === 1 ? t('tours.onePerson') :
                     tour.max_passengers === 2 ? t('tours.twoPeople') :
                     `${language === 'es' ? 'Hasta' : 'Up to'} ${tour.max_passengers}`}
                  </span>
                </div>
                <div className="flex items-center justify-between text-gray-700">
                  <span className="flex items-center gap-1.5">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    {language === 'es' ? 'Categoría' : 'Category'}
                  </span>
                  <span className="font-medium capitalize">{tour.category}</span>
                </div>
              </div>

              <Link
                to={`/booking/${tour.id}`}
                data-testid="tour-detail-book-btn"
                className="w-full h-12 bg-[#F08A5D] hover:bg-[#E0794C] text-white rounded-full font-semibold flex items-center justify-center gap-2 transition-all active:scale-95"
              >
                {t('tours.bookNow')}
                <ChevronRight className="w-4 h-4" />
              </Link>

              <p className="text-xs text-gray-500 text-center mt-3">
                {language === 'es' ? 'Reserva segura · Pago por Stripe o PayPal' : 'Secure booking · Pay via Stripe or PayPal'}
              </p>
            </div>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TourDetailPage;
